import React, { useRef, useEffect, useState, Suspense, lazy } from 'react';
import { ParsedBlock } from '../../services/umbracoApi';
import { mediaUrl, optimizeImageUrl, optimizeHtmlImages } from '../../config';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { InstagramFeedBlock } from './InstagramFeedBlock';
import styles from './BlockGrid.module.css';

const ReviewsSection = lazy(() => import('../Reviews/ReviewsSection').then(m => ({ default: m.ReviewsSection })));
const ShopProductListing = lazy(() => import('../ShopProductListing/ShopProductListing').then(m => ({ default: m.ShopProductListing })));

/** Wrapper that reveals children with a CSS animation when scrolled into view. */
const AnimatedBlock: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`${styles.animatedBlock} ${visible ? styles.animatedBlockVisible : ''} ${className || ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/** Converts Umbraco rich text content (string, {markup}, or {elements} tree) to an HTML string. */
const renderRichText = (content: any): string => {
  if (typeof content === 'string') return content;
  if (content?.markup) return content.markup;
  if (!content || !content.elements) return '';

  const processElement = (el: any): string => {
    if (el.tag === '#text') return el.text || '';
    if (el.tag === '#root') {
      return el.elements?.map(processElement).join('') || '';
    }
    const children = el.elements?.map(processElement).join('') || '';
    const attrs = el.attributes ? Object.entries(el.attributes)
      .map(([key, val]) => `${key}="${val}"`)
      .join(' ') : '';
    return `<${el.tag}${attrs ? ' ' + attrs : ''}>${children}</${el.tag}>`;
  };

  return processElement(content);
};

/** Carousel for testimonial blocks with prev/next buttons and dots. */
const TestimonialCarousel: React.FC<{ children: React.ReactNode[]; }> = ({ children }) => {
  const [current, setCurrent] = useState(0);
  const count = children.length;
  const prev = () => setCurrent((c) => (c - 1 + count) % count);
  const next = () => setCurrent((c) => (c + 1) % count);

  return (
    <div className={styles.testimonialCarousel}>
      <button className={`${styles.carouselBtn} ${styles.carouselPrev}`} onClick={prev} aria-label="Previous">&#8249;</button>
      <button className={`${styles.carouselBtn} ${styles.carouselNext}`} onClick={next} aria-label="Next">&#8250;</button>
      <div className={styles.carouselTrackContainer}>
        <div className={styles.carouselTrack} style={{ transform: `translateX(-${current * 100}%)` }}>
          {children.map((child, i) => (
            <div key={i} className={styles.carouselSlide}>{child}</div>
          ))}
        </div>
      </div>
      <div className={styles.carouselDots}>
        {children.map((_, i) => (
          <button
            key={i}
            className={`${styles.carouselDot} ${i === current ? styles.carouselDotActive : ''}`}
            onClick={() => setCurrent(i)}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

interface BlockGridProps {
  blocks: ParsedBlock[];
}

export const BlockGrid: React.FC<BlockGridProps> = ({ blocks }) => {
  const { settings } = useSiteSettings();
  const renderBlock = (block: ParsedBlock, index: number) => {
    const { contentType, properties, areas } = block;

    switch (contentType) {
      case 'heroBlock': {
        const bgImage = mediaUrl(properties.backgroundImageUrl || properties.backgroundImage?.[0]?.url);
        const bgStyle: React.CSSProperties = bgImage
          ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat' }
          : {};
        return (
          <section key={index} id="hero" className={styles.hero} style={bgStyle}>
            <div className={styles.heroOverlay}></div>
            <div className={styles.heroContent}>
              <h1 className={styles.heroHeading}>{properties.heading}</h1>
              {properties.subheading && <p className={styles.heroSubheading}>{properties.subheading}</p>}
              {(properties.primaryButtonText || properties.secondaryButtonText) && (
                <div className={styles.heroButtons}>
                  {properties.primaryButtonText && properties.primaryButtonLink && (
                    <a href={properties.primaryButtonLink} className={`${styles.heroPrimaryButton} ${styles.heroBtn}`}>
                      {properties.primaryButtonText}
                    </a>
                  )}
                  {properties.secondaryButtonText && properties.secondaryButtonLink && (
                    <a href={properties.secondaryButtonLink} className={styles.heroSecondaryButton}>
                      {properties.secondaryButtonText}
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'sectionHeaderBlock':
        return (
          <header key={index} className={styles.sectionHeader} id={properties.anchorId}>
            <h2>{properties.title}</h2>
            {properties.subtitle && <p>{properties.subtitle}</p>}
          </header>
        );

      case 'richTextBlock':
        return (
          <div
            key={index}
            className={styles.richText}
            dangerouslySetInnerHTML={{ __html: optimizeHtmlImages(renderRichText(properties.content)) }}
          />
        );

      case 'imageBlock':
        return (
          <div key={index} className={styles.imageBlock}>
            {properties.image?.[0] && (
              <img src={mediaUrl(properties.image[0].url)} alt={properties.altText || properties.caption || ''} />
            )}
            {properties.caption && <p className={styles.caption}>{properties.caption}</p>}
          </div>
        );

      case 'galleryBlock': {
        // Images come from the media picker and/or the imageUrls text property (one URL per line).
        const pickerImages: string[] = (Array.isArray(properties.images) ? properties.images : [])
          .map((img: any) => mediaUrl(img?.url))
          .filter(Boolean);
        const urlImages: string[] = (properties.imageUrls || '')
          .split('\n')
          .map((line: string) => line.trim())
          .filter(Boolean)
          .map((url: string) => mediaUrl(url));
        const galleryImages = [...pickerImages, ...urlImages];
        if (galleryImages.length === 0) return null;
        return (
          <section key={index} className={styles.galleryBlock}>
            {properties.title && <h2 className={styles.galleryTitle}>{properties.title}</h2>}
            <div className={styles.galleryGrid}>
              {galleryImages.map((src, i) => (
                <figure key={i} className={styles.galleryItem}>
                  <img
                    src={optimizeImageUrl(src, 700)}
                    alt={properties.title ? `${properties.title} ${i + 1}` : `Galeria ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                  />
                </figure>
              ))}
            </div>
          </section>
        );
      }

      case 'instagramFeedBlock':
        return (
          <InstagramFeedBlock
            key={index}
            heading={properties.heading}
            showCaptions={!!properties.showCaptions}
            ctaButtonText={properties.ctaButtonText}
          />
        );

      case 'htmlEmbedBlock':
        return (
          <div
            key={index}
            className={properties.cssClass || undefined}
            dangerouslySetInnerHTML={{ __html: optimizeHtmlImages(properties.htmlCode || '') }}
          />
        );

      case 'featureCardBlock': {
        const featureImageUrl = mediaUrl(typeof properties.image === 'string'
          ? properties.image
          : properties.image?.[0]?.url || properties.imageUrl);
        const featureInner = (
          <>
            {featureImageUrl && (
              <div className={styles.featureImage}>
                <img src={featureImageUrl} alt={properties.title || ''} />
              </div>
            )}
            <div className={styles.featureContent}>
              {properties.icon && !featureImageUrl && (
                <div className={styles.icon}>{properties.icon}</div>
              )}
              <h3 className={styles.featureTitle}>{properties.title}</h3>
              {properties.description && <p className={styles.featureDescription}>{properties.description}</p>}
              {properties.tag && <span className={styles.featureTag}>{properties.tag}</span>}
            </div>
          </>
        );
        // When a linkUrl is set, the whole card becomes a clickable link.
        return properties.linkUrl ? (
          <a key={index} href={properties.linkUrl} className={`${styles.featureCard} ${styles.featureCardLink}`}>
            {featureInner}
          </a>
        ) : (
          <div key={index} className={styles.featureCard}>
            {featureInner}
          </div>
        );
      }

      case 'statBlock':
        return (
          <div key={index} className={styles.statBlock}>
            <div className={styles.statNumber}>{properties.number || properties.value}</div>
            <div className={styles.statLabel}>{properties.label || properties.title}</div>
          </div>
        );

      case 'pricingCardBlock': {
        const isHighlighted = properties.isHighlighted || properties.highlighted;
        const priceUnit = properties.priceUnit || '';
        const priceUnitDisplay = priceUnit.startsWith('/') ? priceUnit : `/ ${priceUnit}`;
        return (
          <div key={index} className={`${styles.pricingCard} ${isHighlighted ? styles.pricingCardHighlighted : ''}`}>
            <div className={styles.pricingHeader}>
              {properties.highlightLabel && <span className={styles.highlightLabel}>{properties.highlightLabel}</span>}
              <h2 className={styles.pricingTitle}>{properties.title}</h2>
              {properties.subtitle && <p className={styles.pricingSubtitle}>{properties.subtitle}</p>}
              <div className={styles.pricingPrice}>
                {properties.price}<span className={styles.priceUnit}> {priceUnitDisplay}</span>
              </div>
            </div>
            {properties.features && (
              <ul className={styles.pricingFeaturesList}>
                {(Array.isArray(properties.features) ? properties.features : properties.features.split('\n')).map((feature: string, i: number) => {
                  const trimmed = typeof feature === 'string' ? feature.trim() : feature;
                  const isFeatureHighlighted = typeof trimmed === 'string' && trimmed.startsWith('!');
                  const featureText = isFeatureHighlighted ? trimmed.slice(1) : trimmed;
                  return (
                    <li key={i} className={isFeatureHighlighted ? styles.highlightedFeature : ''}>
                      {featureText}
                    </li>
                  );
                })}
              </ul>
            )}
            {properties.buttonText && (
              <a
                href={properties.buttonLink || '/rezerwacja'}
                className={`${styles.pricingCtaButton} ${isHighlighted ? styles.pricingCtaHighlighted : ''}`}
              >
                {properties.buttonText}
              </a>
            )}
          </div>
        );
      }

      case 'pricingBlock': {
        const priceBg = mediaUrl(properties.backgroundImageUrl || properties.backgroundImage);
        const priceInner = (
          <>
            <h3>{properties.categoryName}</h3>
            <div className={styles.priceList}>
              {properties.priceItems?.split('\n').map((item: string, i: number) => {
                const [service, price] = item.split('|').map((s: string) => s.trim());
                return (
                  <div key={i} className={styles.priceItem}>
                    <span>{service}</span>
                    <span>{price}</span>
                  </div>
                );
              })}
            </div>
            {properties.note && <p className={styles.note}>{properties.note}</p>}
          </>
        );
        // With a background image, render as a full-width image band.
        return priceBg ? (
          <div
            key={index}
            className={`${styles.pricingBlock} ${styles.pricingBlockImage}`}
            style={{
              backgroundImage: `url(${priceBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <div className={styles.pricingBlockImageInner}>{priceInner}</div>
          </div>
        ) : (
          <div key={index} className={styles.pricingBlock}>{priceInner}</div>
        );
      }

      case 'ctaButtonBlock':
        return (
          <div key={index} className={styles.ctaButton}>
            <a
              href={properties.buttonLink}
              className={`${styles.button} ${styles[properties.buttonStyle || 'primary']}`}
            >
              {properties.buttonText}
            </a>
          </div>
        );

      case 'contactInfoBlock':
        return (
          <div key={index} className={styles.contactInfo}>
            {properties.title && <h3>{properties.title}</h3>}
            {properties.address && (
              <div className={styles.contactItem}>
                <span className={styles.contactIcon}>📍</span>
                <span>{properties.address}</span>
              </div>
            )}
            {properties.phone && (
              <div className={styles.contactItem}>
                <span className={styles.contactIcon}>📞</span>
                <a href={`tel:${properties.phone.replace(/\s/g, '')}`}>{properties.phone}</a>
              </div>
            )}
            {properties.email && (
              <div className={styles.contactItem}>
                <span className={styles.contactIcon}>✉️</span>
                <a href={`mailto:${properties.email}`}>{properties.email}</a>
              </div>
            )}
            {properties.additionalInfo && <p className={styles.additionalInfo}>{properties.additionalInfo}</p>}
            {(settings.facebookUrl || settings.instagramUrl) && (
              <div className={styles.socialSection}>
                <p className={styles.socialLabel}>Obserwuj mnie:</p>
                <div className={styles.socialLinks}>
                  {settings.facebookUrl && (
                    <a href={settings.facebookUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink} aria-label="Facebook">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                      </svg>
                    </a>
                  )}
                  {settings.instagramUrl && (
                    <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className={styles.socialLink} aria-label="Instagram">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                      </svg>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'teamMemberBlock':
        return (
          <div key={index} className={styles.teamMember}>
            {(properties.photoUrl || properties.photo?.[0]?.url) && (
              <div className={styles.teamMemberImageWrapper}>
                <img
                  alt={properties.name}
                  className={styles.teamMemberImage}
                  src={mediaUrl(properties.photoUrl || properties.photo[0].url)}
                />
              </div>
            )}
            <div className={styles.teamMemberInfo}>
              <h3 className={styles.teamMemberName}>{properties.name}</h3>
              {properties.role && <p className={styles.teamMemberRole}>{properties.role}</p>}
            </div>
          </div>
        );

      case 'teamMemberZigzagBlock':
        return (
          <div key={index} className={`${styles.teamMemberZigzag} ${properties.imagePosition === 'right' ? styles.imageRight : styles.imageLeft}`}>
            {properties.photoUrl && (
              <div className={styles.photo}>
                <img src={mediaUrl(properties.photoUrl)} alt={properties.name} />
              </div>
            )}
            <div className={styles.zigzagContent}>
              <h3>{properties.name}</h3>
              {properties.role && <p className={styles.role}>{properties.role}</p>}
              {properties.bio && <p>{properties.bio}</p>}
              {properties.skills && <p className={styles.skills}>{properties.skills}</p>}
            </div>
          </div>
        );

      case 'fullWidthFeatureBlock': {
        const fwImageUrl = mediaUrl(typeof properties.image === 'string'
          ? properties.image
          : properties.image?.[0]?.url || properties.imageUrl);
        return (
          <div key={index} className={`${styles.featureSection} ${properties.imagePosition === 'right' ? styles.reverse : ''}`}>
            {fwImageUrl && (
              <div className={styles.featureSectionImage} style={{ backgroundImage: `url(${fwImageUrl})` }}></div>
            )}
            <div className={styles.featureSectionText}>
              {properties.sectionHeader && <span className={styles.featureSectionHeader}>{properties.sectionHeader}</span>}
              {properties.title && <h3 className={styles.featureSectionTitle}>{properties.title}</h3>}
              {properties.description && <p className={styles.featureSectionDescription}>{properties.description}</p>}
            </div>
          </div>
        );
      }

      case 'quoteDividerBlock':
        return (
          <div key={index} className={styles.quoteDivider}>
            <h4 className={styles.quote}>
              {properties.highlightedWord && properties.quote?.includes(properties.highlightedWord) ? (
                <>
                  {properties.quote.split(properties.highlightedWord).map((part: string, i: number, arr: string[]) => (
                    <React.Fragment key={i}>
                      {part}
                      {i < arr.length - 1 && <span className={styles.highlight}>{properties.highlightedWord}</span>}
                    </React.Fragment>
                  ))}
                </>
              ) : (
                properties.quote
              )}
            </h4>
            {properties.buttonText && properties.buttonLink && (
              <a href={properties.buttonLink} className={styles.quoteCta}>
                {properties.buttonText}
              </a>
            )}
          </div>
        );

      case 'reviewsBlock':
        return (
          <Suspense key={index} fallback={null}>
            <ReviewsSection variant="cards" />
          </Suspense>
        );

      case 'productListingBlock':
        return (
          <Suspense key={index} fallback={null}>
            <ShopProductListing heading={properties.heading} subtitle={properties.subtitle} />
          </Suspense>
        );

      // Grid rows
      case 'gridRowFull':
      case 'gridRow66':
      case 'gridRow39':
      case 'gridRow93':
      case 'gridRow282':
      case 'gridRow444':
      case 'gridRow3333':
        if (!areas || areas.length === 0) return null;
        return (
          <div key={index} className={styles.rowWrapper}>
            <div className={styles.gridRow}>
              {areas.map((area, areaIndex) => {
                const colSpanClass = area.columnSpan ? styles[`colSpan${area.columnSpan}`] : styles.gridArea;
                return (
                  <div key={areaIndex} className={colSpanClass || styles.gridArea}>
                    {area.items.map((childBlock, childIndex) =>
                      renderBlock(childBlock, childIndex)
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'testimonialBlock': {
        const initial = properties.authorName ? properties.authorName.charAt(0).toUpperCase() : '?';
        return (
          <div key={index} className={styles.testimonialCard}>
            {properties.tag && <span className={styles.testimonialTag}>{properties.tag}</span>}
            {properties.title && <h3 className={styles.testimonialTitle}>{properties.title}</h3>}
            <p className={styles.testimonialQuote}>{properties.quote}</p>
            <div className={styles.testimonialAuthor}>
              <div className={styles.testimonialInitial}>{initial}</div>
              <div className={styles.testimonialAuthorInfo}>
                <strong className={styles.testimonialAuthorName}>{properties.authorName}</strong>
                {properties.authorSubtitle && <span className={styles.testimonialAuthorSubtitle}>{properties.authorSubtitle}</span>}
              </div>
            </div>
          </div>
        );
      }

      case 'accordionItem':
        return (
          <details key={index} className={styles.accordionItem}>
            <summary className={styles.accordionSummary}>{properties.title}</summary>
            <div
              className={styles.accordionContent}
              dangerouslySetInnerHTML={{ __html: optimizeHtmlImages(renderRichText(properties.content)) }}
            />
          </details>
        );

      case 'blogCardBlock': {
        const blogImageUrl = optimizeImageUrl(mediaUrl(properties.imageUrl), 900);
        return (
          <div key={index} className={styles.blogCard}>
            {blogImageUrl && (
              <div className={styles.blogCardImageWrapper}>
                <img
                  src={blogImageUrl}
                  alt={properties.title || ''}
                  className={styles.blogCardImage}
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <div className={styles.blogCardContent}>
              {properties.date && <span className={styles.blogCardDate}>{properties.date}</span>}
              {properties.tag && <span className={styles.blogCardTag}>{properties.tag}</span>}
              <h3 className={styles.blogCardTitle}>{properties.title}</h3>
              {properties.excerpt && <p className={styles.blogCardExcerpt}>{properties.excerpt}</p>}
              {properties.readMoreLink && (
                <a href={properties.readMoreLink} className={styles.blogCardLink}>
                  Czytaj dalej &rarr;
                </a>
              )}
            </div>
          </div>
        );
      }

      default:
        console.warn(`Unknown block type: ${contentType}`);
        return null;
    }
  };

  // Block types that should be auto-grouped into grid rows when consecutive
  const groupableTypes = new Set(['featureCardBlock', 'pricingCardBlock', 'statBlock', 'teamMemberBlock', 'blogCardBlock']);

  // Block types that auto-group vertically (stacked) rather than in grid columns
  const stackableTypes = new Set(['accordionItem']);

  // Block types that render as a carousel when consecutive
  const carouselTypes = new Set(['testimonialBlock']);

  // Block types that skip animation (visible immediately)
  const noAnimateTypes = new Set(['heroBlock']);

  // Group consecutive blocks of the same groupable type into grid rows
  const renderBlocks = () => {
    const result: React.ReactNode[] = [];
    let i = 0;

    while (i < blocks.length) {
      const block = blocks[i];
      const type = block.contentType;

      // Carousel types: group consecutive same-type blocks into a carousel
      if (carouselTypes.has(type) && !block.areas?.length) {
        const group: ParsedBlock[] = [block];
        let j = i + 1;
        while (j < blocks.length && blocks[j].contentType === type && !blocks[j].areas?.length) {
          group.push(blocks[j]);
          j++;
        }

        if (group.length > 1) {
          result.push(
            <AnimatedBlock key={`carousel-${i}`}>
              <TestimonialCarousel>
                {group.map((groupBlock, gi) => renderBlock(groupBlock, gi))}
              </TestimonialCarousel>
            </AnimatedBlock>
          );
          i = j;
          continue;
        }
      }

      // Stackable types: group consecutive same-type blocks into a vertical stack
      if (stackableTypes.has(type) && !block.areas?.length) {
        const group: ParsedBlock[] = [block];
        let j = i + 1;
        while (j < blocks.length && blocks[j].contentType === type && !blocks[j].areas?.length) {
          group.push(blocks[j]);
          j++;
        }

        if (group.length > 1) {
          result.push(
            <AnimatedBlock key={`stack-${i}`}>
              <div className={styles.stackGroup}>
                {group.map((groupBlock, gi) => renderBlock(groupBlock, gi))}
              </div>
            </AnimatedBlock>
          );
          i = j;
          continue;
        }
      }

      // Groupable types: group consecutive same-type blocks into grid columns
      if (groupableTypes.has(type) && !block.areas?.length) {
        const group: ParsedBlock[] = [block];
        let j = i + 1;
        while (j < blocks.length && blocks[j].contentType === type && !blocks[j].areas?.length) {
          group.push(blocks[j]);
          j++;
        }

        if (group.length > 1) {
          const colSpan = Math.floor(12 / group.length);
          result.push(
            <AnimatedBlock key={`group-${i}`}>
              <div className={styles.rowWrapper}>
                <div className={styles.gridRow}>
                  {group.map((groupBlock, gi) => (
                    <div key={gi} className={styles[`colSpan${colSpan}`] || styles.gridArea}>
                      {renderBlock(groupBlock, gi)}
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedBlock>
          );
          i = j;
          continue;
        }
      }

      if (noAnimateTypes.has(type)) {
        result.push(renderBlock(block, i));
      } else {
        result.push(
          <AnimatedBlock key={`anim-${i}`}>
            {renderBlock(block, i)}
          </AnimatedBlock>
        );
      }
      i++;
    }

    return result;
  };

  return (
    <div className={styles.blockGrid}>
      {renderBlocks()}
    </div>
  );
};
